import {useField, useFormikContext} from 'formik';
import React, {useCallback, useEffect, useState} from 'react';
import AccessibleTypeahead from 'shared/components/accessible-typeahead';
import AssetService, {CountryOption} from 'src/services/AssetService';
import GooglePlacesService from 'src/services/google-places-service';
import {
  GooglePlacesAddressDetails,
  GooglePlacesPrediction,
} from 'src/services/google-places-service/google-places-service';
import {FormOption} from 'src/types/form';
import {AddressForm} from 'src/types/onboarding';
import {includesOption, getCountryOptions} from 'src/utils/helpers';

function determineSubdivision(
  current: string,
  details: GooglePlacesAddressDetails,
  subdivisions: FormOption[]
): string {
  return includesOption(subdivisions, details.region)
    ? details.region
    : includesOption(subdivisions, details.country)
    ? details.country
    : current;
}

interface Props {
  focus?: boolean;
  fieldNames: {
    addressLine1: string;
    city: string;
    subdivision: string;
    postalCode: string;
    country: string;
  };
}

export function AddressLineAutoComplete({
  focus,
  fieldNames = {
    addressLine1: 'addressLine1',
    city: 'city',
    subdivision: 'subdivision',
    postalCode: 'postalCode',
    country: 'country',
  },
}: Props) {
  const form = useFormikContext<AddressForm>();
  const [addressLine1, addressLine1Meta, {setValue: setAddressLine1}] = useField(
    fieldNames.addressLine1
  );
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [subdivisions, setSubdivisions] = useState<FormOption[]>([]);
  const formCountry = form.values[fieldNames.country];

  const [prefetchedDetails, setPrefetchedDetails] = useState<
    Map<string, GooglePlacesAddressDetails>
  >(new Map());

  const prefetchAllResults = useCallback((predictions: GooglePlacesPrediction[]) => {
    predictions.forEach(async (prediction) => {
      const details = await GooglePlacesService.getAddressDetails(prediction.place_id);
      setPrefetchedDetails((prev) => new Map(prev).set(prediction.place_id, details));
    });
  }, []);

  const onInputChange = useCallback(
    (_: unknown, event: React.ChangeEvent) => {
      addressLine1.onChange(event);
    },
    [addressLine1]
  );

  useEffect(() => {
    getCountryOptions().then((response) => {
      setCountries(response);
    });
  }, []);

  useEffect(() => {
    AssetService.getSubdivisionOptions(formCountry).then((response) => {
      setSubdivisions(response);
    });
  }, [formCountry]);

  const onSearch = async (query: string): Promise<GooglePlacesPrediction[]> => {
    const predictions = await GooglePlacesService.getPredictions(query);
    prefetchAllResults(predictions);
    return predictions;
  };

  async function onSelect(result: GooglePlacesPrediction) {
    setAddressLine1(result.description.split(',')[0]);

    const cachedDetails = prefetchedDetails.get(result.place_id);

    if (cachedDetails) {
      processAddressDetails(cachedDetails);
    } else {
      try {
        const details = await GooglePlacesService.getAddressDetails(result.place_id);
        if (details) {
          processAddressDetails(details);
        }
      } catch (error) {
        console.error('Failed to fetch address details:', error);
      }
    }
  }

  async function processAddressDetails(details: GooglePlacesAddressDetails) {
    let newCountry = formCountry,
      newSubdivision = subdivisions;

    if (formCountry !== details.country) {
      newCountry = includesOption(countries, details.country) ? details.country : 'US';

      if (newCountry !== formCountry) {
        try {
          newSubdivision = await AssetService.getSubdivisionOptions(newCountry);
          updateFormFields(details, newCountry, newSubdivision);
        } catch (error) {
          console.error('Failed to fetch subdivision options:', error);
        }
        return;
      }
    }

    updateFormFields(details, newCountry, newSubdivision);
  }

  function updateFormFields(
    details: GooglePlacesAddressDetails,
    country: string,
    subdivisionOptions: FormOption[]
  ) {
    form.setTouched(
      {
        [fieldNames.country]: true,
        [fieldNames.city]: true,
        [fieldNames.addressLine1]: true,
        [fieldNames.postalCode]: true,
        [fieldNames.subdivision]: true,
      },
      false
    );

    form.setValues({
      ...form.values,
      [fieldNames.country]: country || '',
      [fieldNames.city]: details.city || '',
      [fieldNames.addressLine1]: details.address || '',
      [fieldNames.postalCode]: details.postal_code || '',
      [fieldNames.subdivision]:
        determineSubdivision(form.values[fieldNames.subdivision], details, subdivisionOptions) ||
        '',
    });
  }

  return (
    <AccessibleTypeahead
      id="address-line1-typeahead"
      error={addressLine1Meta.touched && addressLine1Meta.error}
      showCheck
      required
      cy="addressLine1"
      onSearch={onSearch}
      onSelect={onSelect}
      value={addressLine1.value}
      aria-label="Street Address"
      onInputChange={onInputChange}
      focusOnMount={focus ? !addressLine1.value : false}
      labelKey="description"
      inputProps={{name: fieldNames.addressLine1, autoComplete: 'address-line1'}}
      placeholder="e.g. 221b Baker Street"
      debounceDelay={450}
    />
  );
}
